import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ShieldCheck, 
  Building2, 
  AlertCircle, 
  CheckCircle2, 
  X, 
  FileText, 
  ArrowRight, 
  Lock, 
  ExternalLink,
  HelpCircle,
  FileCheck
} from 'lucide-react';
import { useAuth } from '../AuthContext';
import { centralRfxService, CentralRfxItem, UserRfxItem } from '../services/centralRfxService';

interface StateVendorParticipationModalProps {
  isOpen: boolean;
  onClose: () => void;
  solicitation: CentralRfxItem | null;
  onSuccess: (copiedItem: UserRfxItem) => void;
}

export const StateVendorParticipationModal: React.FC<StateVendorParticipationModalProps> = ({
  isOpen,
  onClose,
  solicitation,
  onSuccess
}) => {
  const { user, vendorProfile, updateStateCredential, updateCurrentProfile } = useAuth();

  const [intentConfirmed, setIntentConfirmed] = useState(false);
  const [attestationChecked, setAttestationChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Form fields for unregistered states
  const [stateVendorId, setStateVendorId] = useState('');
  const [legalEntityName, setLegalEntityName] = useState('');
  const [stateTaxId, setStateTaxId] = useState('');
  const [vendorClassification, setVendorClassification] = useState('Standard Commercial Entity');

  const stateCode = (solicitation?.stateCode || solicitation?.state || 'TX').toUpperCase();
  const stateName = solicitation?.state || stateCode;

  // Determine if user has existing state registration
  const existingRegistration = vendorProfile?.stateRegistrations?.[stateCode] || 
    (vendorProfile as any)?.stateCredentials?.[stateCode] || null;

  const isAlreadyRegistered = Boolean(
    existingRegistration && 
    (existingRegistration.status === 'Active' || existingRegistration.status === 'verified' || existingRegistration.vendorId)
  );

  useEffect(() => {
    if (isOpen) {
      setIntentConfirmed(false);
      setAttestationChecked(false);
      setErrorMessage(null);
      if (vendorProfile?.companyName) {
        setLegalEntityName(vendorProfile.companyName);
      }
      if (existingRegistration?.vendorId) {
        setStateVendorId(existingRegistration.vendorId);
      } else {
        setStateVendorId(`VID-${stateCode}-${Math.floor(100000 + Math.random() * 900000)}`);
      }
      setStateTaxId(vendorProfile?.taxId || `TAX-${stateCode}-8829`);
    }
  }, [isOpen, solicitation, vendorProfile, stateCode, existingRegistration]);

  if (!isOpen || !solicitation) return null;

  const handleConfirmAndCopy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setErrorMessage('You must be signed in to participate in state solicitations.');
      return;
    }

    if (!intentConfirmed) {
      setErrorMessage('Please confirm your intent to participate in this solicitation.');
      return;
    }

    if (!isAlreadyRegistered && !attestationChecked) {
      setErrorMessage('Please attest that your entity is in good legal standing in this state.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const activeVendorId = isAlreadyRegistered 
        ? (existingRegistration.vendorId || existingRegistration.stateVendorId || 'VERIFIED-EXISTING')
        : stateVendorId.trim();

      // 1. If not previously registered in this state, persist state registration in user vendor profile
      if (!isAlreadyRegistered) {
        await updateStateCredential(stateCode, {
          state: stateName,
          stateCode,
          vendorId: activeVendorId,
          legalEntityName: legalEntityName.trim() || vendorProfile?.companyName || 'Registered Enterprise',
          stateTaxId: stateTaxId.trim(),
          classification: vendorClassification,
          status: 'Active',
          registeredAt: new Date().toISOString(),
          lastVerifiedAt: new Date().toISOString()
        } as any);
      }

      // 2. Copy Central RFX record to User's Own Database
      const copiedItem = await centralRfxService.copyToUserDatabase({
        userId: user.uid,
        solicitation,
        stateVendorId: activeVendorId
      });

      // 3. Notify backend participation endpoint
      try {
        await fetch('/api/user-rfx/participate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user.uid,
            solicitation,
            stateVendorId: activeVendorId,
            intentConfirmed: true
          })
        });
      } catch (postErr) {
        console.warn('Backend participate notification error (safe):', postErr);
      }

      setIsSubmitting(false);
      onSuccess(copiedItem);
      onClose();
    } catch (err: any) {
      console.error('Participation copy failed:', err);
      setErrorMessage(err.message || 'Failed to complete state vendor verification and record copying.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-950/60 backdrop-blur-xs">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Header with State Banner */}
        <div className="bg-slate-900 text-white px-6 py-5 flex items-start justify-between relative overflow-hidden">
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 rounded">
                State Procurement Portal Protocol
              </span>
              <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded">
                Jurisdiction: {stateCode}
              </span>
            </div>
            <h2 className="text-lg font-bold tracking-tight text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-indigo-400" />
              State Vendor Verification & Participation Gate
            </h2>
            <p className="text-xs text-slate-300 mt-1 max-w-lg">
              State procurement regulations require verified registered vendor status in {stateName} to access detailed line items, bidding schedules, and participate in this opportunity.
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors cursor-pointer relative z-10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <form onSubmit={handleConfirmAndCopy} className="p-6 overflow-y-auto space-y-6 text-xs text-gray-700">
          {errorMessage && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-xs">Verification Incomplete</p>
                <p className="text-[11px] text-red-600 mt-0.5">{errorMessage}</p>
              </div>
            </div>
          )}

          {/* Opportunity Overview Card */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-mono font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                {solicitation.solicitationId}
              </span>
              <span className="text-[11px] text-gray-500">
                Deadline: <strong className="text-gray-900">{solicitation.dueDate || 'Open'}</strong>
              </span>
            </div>
            <h3 className="text-sm font-bold text-gray-900 leading-snug">
              {solicitation.title}
            </h3>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-600 pt-1">
              <span>Agency: <strong className="text-gray-800">{solicitation.agency}</strong></span>
              <span>Category: <strong className="text-gray-800">{solicitation.category}</strong></span>
              <span>Est. Budget: <strong className="text-emerald-700">${(solicitation.pricing || 75000).toLocaleString()}</strong></span>
            </div>
          </div>

          {/* Section 1: Intent Confirmation */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
              <FileCheck className="w-4 h-4 text-indigo-600" />
              1. Declaration of Formal Intent to Participate
            </h4>
            <label className="flex items-start gap-3 p-3.5 bg-indigo-50/50 border border-indigo-100 rounded-xl cursor-pointer hover:bg-indigo-50 transition-colors">
              <input
                type="checkbox"
                checked={intentConfirmed}
                onChange={(e) => setIntentConfirmed(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
              />
              <div className="text-xs leading-relaxed text-gray-800">
                <span className="font-semibold text-indigo-950 block mb-0.5">
                  Confirm Official Bidder / Proposer Intent
                </span>
                I confirm that our organization intends to review solicitation specifications and prepare a response for <span className="font-semibold">{solicitation.solicitationId}</span> in accordance with State of {stateName} public procurement statutes.
              </div>
            </label>
          </div>

          {/* Section 2: State Vendor Registration Status */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-indigo-600" />
              2. State of {stateName} Vendor Credential Verification
            </h4>

            {isAlreadyRegistered ? (
              /* Already Registered State */
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-bold text-emerald-950 text-xs">
                    Verified Active Vendor in {stateName}
                  </p>
                  <p className="text-[11px] text-emerald-800 leading-relaxed">
                    Your vendor profile is already credentialed with the {stateName} procurement registry under Vendor ID: <strong className="font-mono">{existingRegistration.vendorId || existingRegistration.stateVendorId || 'VID-VERIFIED'}</strong>.
                  </p>
                  <div className="pt-1 flex items-center gap-3 text-[11px] text-emerald-900">
                    <span>Entity: <strong>{existingRegistration.legalEntityName || vendorProfile?.companyName}</strong></span>
                    <span>Status: <strong className="uppercase">{existingRegistration.status || 'Active'}</strong></span>
                  </div>
                </div>
              </div>
            ) : (
              /* Inline State Registration Form */
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-4">
                <div className="flex items-start gap-2.5 text-slate-700">
                  <HelpCircle className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                  <p className="text-[11px] leading-relaxed">
                    No active {stateName} credential was found in your profile. Complete this statutory verification to attest your standing with the {stateName} Comptroller / State Purchasing Division.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                      {stateCode} State Vendor ID (VID / Supplier #) *
                    </label>
                    <input
                      type="text"
                      required
                      value={stateVendorId}
                      onChange={(e) => setStateVendorId(e.target.value)}
                      placeholder={`e.g. ${stateCode}-VID-982103`}
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs text-gray-900 focus:outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                      State Tax ID / SOS Filing # *
                    </label>
                    <input
                      type="text"
                      required
                      value={stateTaxId}
                      onChange={(e) => setStateTaxId(e.target.value)}
                      placeholder="e.g. 12-3456789 or SOS-9021"
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs text-gray-900 focus:outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                      Legal Business Entity Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={legalEntityName}
                      onChange={(e) => setLegalEntityName(e.target.value)}
                      placeholder="Legal Entity Name matching State Registration"
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs text-gray-900 focus:outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                      Procurement Classification / Preference Program
                    </label>
                    <select
                      value={vendorClassification}
                      onChange={(e) => setVendorClassification(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs text-gray-900 focus:outline-none focus:border-indigo-500"
                    >
                      <option value="Standard Commercial Entity">Standard Commercial Entity</option>
                      <option value="Small Business Enterprise (SBE)">Small Business Enterprise (SBE)</option>
                      <option value="Historically Underutilized Business (HUB / MBE / WBE)">Historically Underutilized Business (HUB / MBE / WBE)</option>
                      <option value="Disabled Veteran Business Enterprise (DVBE / SDVOSB)">Disabled Veteran Business Enterprise (DVBE / SDVOSB)</option>
                    </select>
                  </div>
                </div>

                <label className="flex items-start gap-2.5 pt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={attestationChecked}
                    onChange={(e) => setAttestationChecked(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                  <span className="text-[11px] text-gray-600 leading-snug">
                    I attest under penalty of state procurement false claims statutes that our entity is legally authorized to conduct business with state agencies in {stateName} and is not debarred or suspended.
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* Action Footer inside Form */}
          <div className="pt-4 border-t border-gray-100 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting || !intentConfirmed || (!isAlreadyRegistered && !attestationChecked)}
              className="px-5 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-sm flex items-center gap-2 transition-all cursor-pointer"
            >
              {isSubmitting ? (
                <span>Verifying & Copying Record...</span>
              ) : (
                <>
                  <span>Verify & Copy to My RFX Database</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};
